
/**
 * Utility functions for Open Graph images
 */

/**
 * Get the appropriate OG image path based on content type and data
 * @param type The type of content page
 * @param data The data object for the content
 * @returns The path to the appropriate OG image
 */
export function getOgImagePath(
  type: 'offer' | 'seller' | 'custom-package' | 'buyer-request' | 'static',
  data?: any
): string {
  // For static pages, return the path to the static image
  if (type === 'static') {
    return '/og-images/default.png';
  }
  
  // For content pages, check if we have an ID for dynamic image generation
  if (data?.id) {
    // In production, use the dynamic image generation from the edge function
    if (typeof window !== 'undefined') {
      const edgeFunctionUrl = `${window.location.origin}/functions/v1/generate-og-image`;
      return `${edgeFunctionUrl}?type=${type}&id=${data.id}`;
    }
    
    // For local development or as fallback, use static images:
    return `/og-images/${type}.png`;
  }
  
  // Fallback to static images by type
  switch (type) {
    case 'offer':
      return '/og-images/offer.png';
      
    case 'seller':
      return '/og-images/seller.png';
      
    case 'custom-package':
      return '/og-images/custom-package.png';
      
    case 'buyer-request':
      return '/og-images/buyer-request.png';
      
    default:
      return '/og-images/default.png';
  }
}

/**
 * Generate a description for Open Graph meta tags based on content type and data
 * @param type The type of content page
 * @param data The data object for the content
 * @returns A suitable description for OG meta tags
 */
export function generateOgDescription(
  type: 'offer' | 'seller' | 'custom-package' | 'buyer-request' | 'static',
  data?: any
): string {
  switch (type) {
    case 'offer':
      if (data?.dr && data?.da) {
        return `${data.title} - DR ${data.dr}, DA ${data.da} - ${data.link_type || 'SEO'} link from a trusted seller on BackLink Hub`;
      }
      return data?.title || 'Quality backlink offer from a verified seller on BackLink Hub';
      
    case 'seller':
      return `${data?.name || 'Seller'} - ${data?.bio?.substring(0, 100) || 'BackLink Hub verified seller profile'}`;
      
    case 'custom-package':
      return `${data?.title || 'Custom Package'} - Premium backlink package on BackLink Hub`;
      
    case 'buyer-request':
      return `${data?.niche || 'SEO'} link request - Budget: $${data?.budgetMin || 0}-$${data?.budgetMax || 0}`;
      
    default:
      return 'Buy and sell premium SEO backlinks in a transparent marketplace';
  }
}
